/**
 * catalog-requests/render — 요청함 목록 · 요청 상세의 그리기. 순수 함수(상태 → HTML). CSS 접두 cr- = catalog-requests.
 * 목록: 전면 표(제품 목록 pv-table 꼴). 상세: 머리 카드 · 파트넘버 + 표 · 칸 표 전면 · 버튼 셋.
 */
import { CATALOG_TABLE_WORD, TOKEN_ROLE_WORD, DRAFT_SOURCE_WORD, E_CATALOG_REQUEST_FILTERS, E_CATALOG_TABLES, E_REQUEST_PERIODS, PENDING_STATUS_WORD, REQUEST_FILTER_WORD, REQUEST_PERIOD_WORD, catalogTableWord, isResearching } from '../../api/client';
import { ROW_NO_HEAD, rowNoCell } from '../../components/row-no';
import type { CatalogLineView, CatalogNumberMatch, DescriptionReading, CatalogRequestView, CloseReport, ECatalogPendingStatus, ModelColumnsView, TableColumnView } from '../../api/client';
import { renderNotesButton } from '../../components/notes-pop';
import type { NotesPop } from '../../components/notes-pop';
import { renderPagerLine } from '../../components/pager-line';
import { escapeHtml, fmtDateTime } from '../../lib/cells';
import { missingSlotRolesOf, renderSlotTable } from '../../components/slot-reading';
import { SERVER_MADE_COLUMNS, cellKindOf, isNumberShape, numberShapeWordOf, wrongModelNumberColumns, wrongNumberColumns, columnHintOf, columnLabelOf, missingRequired, missingRequiredModel, modelEvidenceKeyOf, optionPartMatchOrNull, orphanEvidenceLabelsOf, rawOf } from './form';
import type { CatalogRequestDetailData, CatalogRequestsData, DraftForm } from './types';

const LIST_COLUMNS = 9;   // 순번 포함
const LINE_COLUMNS = 6;   // 전체 탭 표의 열 수(순번 포함)
/** 예/아니오 열의 라디오 셋 — 값 'true' · 'false' · ''(비움 = NULL. 카탈로그 boolean 열은 전부 NULL 허용) */
const BOOL_CHOICES: readonly (readonly [string, string])[] = [['true', '예'], ['false', '아니오'], ['', '비움']];
/** 상태 → 칩 색. 앱 공통 칩(deal.css 값) — 확인 대기는 호박(chip-info), 보류 파랑(chip-primary), 컨펌 초록, 반려 빨강, 요청됨 회색 */
/** 번호 열 넷의 말 — 맞은 행 줄에 "(스페어 873594-001)" */
const NUMBER_COLUMN_WORD: Record<CatalogNumberMatch['matched_column'], string> = { option_part: '옵션', spare_part: '스페어', assembly_part: '어셈블리', factory_part: '팩토리' };
const STATUS_CHIP_CLASS: Record<ECatalogPendingStatus, string> = {
  pending: 'chip-muted', requested: 'chip-muted', drafted: 'chip-info', on_hold: 'chip-primary', registered: 'chip-success', rejected: 'chip-warning',
};

// ─── 목록 ──────────────────────────────────────────────────────────
/** 탭 다섯 + 찾기(제품 목록 꼴 pc-search, 300ms 디바운스) + 요청날짜 기간(2026-10-19) + 페이저 한 줄 */
export function renderRequestFilters(data: CatalogRequestsData): string {
  const bAll = data.filter === 'all';
  const tabs = E_CATALOG_REQUEST_FILTERS.map((f) =>
    `<button type="button" class="ce-tab${f === data.filter ? ' active' : ''}" data-action="tab" data-filter="${f}">${REQUEST_FILTER_WORD[f]}</button>`);
  const page = bAll ? data.lines : data.list;
  const pager = page === null ? '' : renderPagerLine(page, 'page');
  const search = `<label class="pc-search"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg><input type="search" data-action="query" placeholder="${bAll ? '파트넘버 · 스페어 · 어셈블리 · 디스크립션' : '파트넘버 · 디스크립션 · 요청자'}" value="${escapeHtml(data.q)}"></label>`;
  const period = `<select class="pc-select" data-action="period" aria-label="요청날짜 기간">${E_REQUEST_PERIODS.map((p) => `<option value="${p}" ${p === data.period ? 'selected' : ''}>${REQUEST_PERIOD_WORD[p]}</option>`).join('')}</select>`;
  // 전체 탭은 기간 대신 표(카테고리) 필터 — 확정 행엔 요청날짜가 없다(2026-10-20)
  const table = `<select class="pc-select" data-action="table" aria-label="카테고리"><option value="">전체 카테고리</option>${E_CATALOG_TABLES.map((t) => `<option value="${t}" ${t === data.table ? 'selected' : ''}>${CATALOG_TABLE_WORD[t]}</option>`).join('')}</select>`;
  const close = `<button type="button" class="btn-ghost cr-close-in-catalog" data-action="close-in-catalog" ${data.loading ? 'disabled' : ''} title="옵션 PN 이 카탈로그에 이미 있는 열린 요청을 연결로 닫습니다 — 카탈로그는 안 건드립니다">중복 요청 닫기</button>`;   // 요청 뒤에 행이 생긴 묵은 요청 — 버그로 쌓인 것을 치우는 임시 버튼, 나중에 뺀다(현민 2026-10-20 저녁)
  return `<div class="cr-bar"><div class="ce-tabs">${tabs.join('')}</div>${search}${bAll ? table : period}<span class="pv-spacer"></span>${close}${pager}</div>`;
}

/** [중복 요청 닫기] 의 결과 카드 — 닫음 · 제품 채움 · 건너뜀 수와 건너뛴 건 한 줄씩(#번호는 그 요청 상세, 새 탭). alert 대신(현민 2026-10-20 "가독성 있게") */
export function renderCloseReport(report: CloseReport | null): string {
  if (report === null) return '';
  const skipped = report.skipped.length === 0 ? '' : `<ul class="cr-close-skipped">${report.skipped.map((s) =>
    `<li><a href="/catalog-entry/requests/${s.pending_id}" target="_blank" rel="noopener">#${s.pending_id}</a> — ${escapeHtml(s.why)}</li>`).join('')}</ul>`;
  const none = report.closed === 0 && report.skipped.length === 0 ? '<p class="cr-close-none">닫을 요청이 없습니다 — 카탈로그에 이미 있는 열린 요청이 0건.</p>' : '';
  return `<div class="cr-close-report">
    <div class="cr-close-head">
      <strong>중복 요청 닫기 결과</strong>
      <span class="cr-close-stat">닫음 <b>${report.closed}</b></span>
      <span class="cr-close-stat">제품 연결 <b>${report.products_filled}</b></span>
      <span class="cr-close-stat${report.skipped.length === 0 ? '' : ' is-warn'}">건너뜀 <b>${report.skipped.length}</b></span>
      <span class="pv-spacer"></span>
      <button type="button" class="btn-ghost" data-action="close-report-dismiss" aria-label="결과 닫기">✕</button>
    </div>
    ${none}${skipped}
  </div>`;
}

/** 전체 탭 — 카탈로그 전부(확정 행 먼저 PN 순 → 열린 요청). 파트넘버 · 디스크립션 · 카테고리 · 제조사 · 상태(확정 / 요청 상태). 확정 행은 카탈로그 그 행(드로어), 요청은 요청 상세(2026-10-20) */
export function renderCatalogLineTable(data: CatalogRequestsData): string {
  return `
    <table class="pv-table cr-table">
      <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>디스크립션</th><th>카테고리</th><th>제조사</th><th>상태</th></tr></thead>
      <tbody>${renderCatalogLineRows(data)}</tbody>
    </table>`;
}

function renderCatalogLineRows(data: CatalogRequestsData): string {
  if (data.error !== '')                     return `<tr><td colspan="${LINE_COLUMNS}" class="text-danger">${escapeHtml(data.error)}</td></tr>`;
  if (data.loading || data.lines === null)   return `<tr><td colspan="${LINE_COLUMNS}" class="text-muted">불러오는 중…</td></tr>`;
  if (data.lines.rows.length === 0)          return `<tr><td colspan="${LINE_COLUMNS}" class="text-muted">맞는 줄이 없습니다</td></tr>`;
  return data.lines.rows.map((line, index) => renderCatalogLineRow(line, data.offset + index)).join('');
}

function renderCatalogLineRow(line: CatalogLineView, no: number): string {
  const href = catalogLineHrefOf(line);
  const status = line.status === null ? '<span class="chip chip-success cr-chip">확정</span>' : renderStatusChip(line.status);
  return `
    <tr class="cr-row" data-action="open" data-href="${href}" title="${line.kind === 'row' ? '누르면 카탈로그 그 행(새 탭)' : '누르면 요청 상세(새 탭)'}">
      ${rowNoCell(no, 0)}
      <td class="pl-main mono"><a class="cr-pn" href="${href}" target="_blank"><b>${line.option_part === null ? '(PN 없음)' : escapeHtml(line.option_part)}</b></a></td>
      <td class="pl-main">${line.description === null ? '' : escapeHtml(line.description)}</td>
      <td class="pl-sub">${line.table === null ? '—' : escapeHtml(catalogTableWord(line.table))}</td>
      <td class="pl-sub">${line.vendor === null ? '—' : escapeHtml(line.vendor)}</td>
      <td>${status}</td>
    </tr>`;
}

/** 확정 행 → 전체 검색에서 그 PN 의 드로어(표 페이지가 아니라 — 카테고리를 옮겨도 PN 으로 찾는다, 2026-10-20). 요청 → 요청 상세 */
function catalogLineHrefOf(line: CatalogLineView): string {
  if (line.kind === 'pending') return `/catalog-entry/requests/${line.id}`;
  return catalogSearchHrefOf(line.option_part === null ? '' : line.option_part, line.table === null ? '' : line.table);
}

/** 전체 검색 `/catalog?q=PN&table=표` — CatalogSearchView 가 그 표의 줄(없으면 유일한 줄)의 드로어를 바로 연다 */
function catalogSearchHrefOf(optionPart: string, table: string): string {
  const params = new URLSearchParams();
  if (optionPart !== '') params.set('q', optionPart);
  if (table !== '') params.set('table', table);
  const query = params.toString();
  return query === '' ? '/catalog' : `/catalog?${query}`;
}

/** 전면 표 — 파트넘버 · 디스크립션 · 메모 · 카테고리 · 출처 · 상태 · 요청자 · 요청날짜(머리말 현민 2026-10-14 밤. 초안 열은 뺌. 메모는 사람의 운영 메모, 2026-10-18 회의 "목록에 보이게"). 줄 전체가 상세(새 탭) — 컨펌은 거기서 */
export function renderRequestTable(data: CatalogRequestsData): string {
  return `
    <table class="pv-table cr-table">
      <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>디스크립션</th><th>메모</th><th>카테고리</th><th>출처</th><th>상태</th><th>요청자</th><th>요청날짜</th></tr></thead>
      <tbody>${renderRequestRows(data)}</tbody>
    </table>`;
}

function renderRequestRows(data: CatalogRequestsData): string {
  if (data.error !== '')                    return `<tr><td colspan="${LIST_COLUMNS}" class="text-danger">${escapeHtml(data.error)}</td></tr>`;
  if (data.loading || data.list === null)   return `<tr><td colspan="${LIST_COLUMNS}" class="text-muted">불러오는 중…</td></tr>`;
  if (data.list.rows.length === 0)          return `<tr><td colspan="${LIST_COLUMNS}" class="text-muted">맞는 줄이 없습니다</td></tr>`;
  return data.list.rows.map((row, index) => renderRequestRow(row, data.notesPop, data.offset + index)).join('');
}

function renderRequestRow(row: CatalogRequestView, popOrNull: NotesPop | null, no: number): string {
  const href = `/catalog-entry/requests/${row.pending_id}`;
  return `
    <tr class="cr-row" data-action="open" data-href="${href}" title="누르면 상세(새 탭) — 칸을 보고 컨펌 · 승격">
      ${rowNoCell(no, 0)}
      <td class="pl-main mono"><a class="cr-pn" href="${href}" target="_blank"><b>${row.option_part === null ? '(PN 없음)' : escapeHtml(row.option_part)}</b></a></td>
      <td class="pl-main">${escapeHtml(row.requested_text ?? '')}</td>
      <td class="pl-sub cr-notes-cell"><div class="cr-notes-wrap">
        ${row.notes === null ? '' : `<span class="cr-notes-text" title="${escapeHtml(row.notes)}">${escapeHtml(row.notes)}</span>`}
        ${renderNotesButton(String(row.pending_id), row.notes, popOrNull)}
      </div></td>
      <td class="pl-sub">${row.catalog_table_name === null ? '—' : escapeHtml(catalogTableWord(row.catalog_table_name))}</td>
      <td class="pl-sub cr-nowrap">${row.draft_source === null ? '—' : DRAFT_SOURCE_WORD[row.draft_source]}</td>
      <td class="cr-nowrap">${renderStatusChip(row.status)}</td>
      <td class="pl-sub cr-nowrap">${row.requested_by === null ? '—' : escapeHtml(row.requested_by)}</td>
      <td class="pl-sub mono">${escapeHtml(fmtDateTime(row.created_at))}</td>
    </tr>`;
}

/** 상태 칩 — 상태만, 한 줄 고정. 출처는 따로 열 */
function renderStatusChip(status: ECatalogPendingStatus): string {
  return `<span class="chip ${STATUS_CHIP_CLASS[status]} cr-chip">${PENDING_STATUS_WORD[status]}</span>`;
}

// ─── 상세 ──────────────────────────────────────────────────────────
export function renderRequestDetail(data: CatalogRequestDetailData): string {
  if (data.loading)       return `<div class="text-muted">불러오는 중…</div>`;
  if (data.view === null) return `<div class="alert alert-error">${escapeHtml(data.error === '' ? '없는 요청' : data.error)}</div>`;
  const view = data.view;
  const bOpen = isOpenView(view);
  const inCatalog = optionPartMatchOrNull(data.match);   // 옵션 PN 이 카탈로그에 이미 있음 — 승격 대신 [요청 종료] + 링크(2026-10-20 밤)
  return `
    <div class="cr-top">${renderDetailHead(view, bOpen)}${renderNotesCard(data)}</div>
    ${data.error === '' ? '' : `<div class="alert alert-error">${escapeHtml(data.error)}</div>`}
    <div class="form-card">
      <div class="form-card-head"><h2>초안</h2><small>파트넘버와 카테고리를 정하면 그 카테고리의 칸이 나옵니다. 필수 칸(*)이 다 차야 승격됩니다</small></div>
      <div class="cr-form-row">
        <label class="form-field"><span>파트넘버 <b class="cr-req">*</b>${inCatalog === null ? '' : ` <a class="cr-catalog-link" href="${catalogRowHrefOf(inCatalog)}" target="_blank" rel="noopener">해당 카탈로그 수정 ↗</a>`}</span><input type="text" class="input mono" data-action="pn" value="${escapeHtml(data.form.option_part)}" ${bOpen ? '' : 'disabled'}></label>
        <label class="form-field"><span>카테고리</span>
          <select class="input" data-action="table" ${bOpen ? '' : 'disabled'}>
            <option value=""${data.form.table === null ? ' selected' : ''}>고르기</option>
            ${E_CATALOG_TABLES.map((table) => `<option value="${table}"${table === data.form.table ? ' selected' : ''}>${CATALOG_TABLE_WORD[table]}</option>`).join('')}
          </select>
        </label>
      </div>
      ${renderMatchLine(data, bOpen)}
      ${renderDraftCells(data.form, data.columns, bOpen)}
      <div class="cr-reading">${renderDescriptionReading(data)}</div>
    </div>
    ${renderModelSection(data, bOpen)}
    <div class="cr-actions-slot">${renderActionsOf(data)}</div>`;
}

/** 버튼 바 — 상세 전체와 약식 읽기가 같이 그린다(읽기가 오면 이것만 다시). view 가 없으면 '' */
export function renderActionsOf(data: CatalogRequestDetailData): string {
  if (data.view === null) return '';
  return renderDetailActions(data, isOpenView(data.view), optionPartMatchOrNull(data.match));
}

/** 지난 요청만 읽기. 조사 중도 연다 — 사람이 저장한 칸은 루틴이 안 덮는다(2026-10-20) */
function isOpenView(view: CatalogRequestView): boolean {
  return view.status !== 'registered' && view.status !== 'rejected';
}

/** 약식 상자 — 원문을 그 카테고리의 기본 규칙으로 읽은 자리들 + 저장될 약식(서버가 승격 때 똑같이 만든다. 2026-10-26 승격도 규칙이) */
export function renderDescriptionReading(data: CatalogRequestDetailData): string {
  if (data.form.table === null) return '';
  const head = '<span class="form-label">약식 — 원문을 기본 규칙으로 읽어 만듭니다</span>';
  if (rawOf(data.form) === '') return `${head}<p class="pc-note is-missing">원문이 비었습니다 — 약식은 원문에서 만듭니다</p>`;
  const reading = data.reading;
  if (reading === null) return `${head}<p class="pc-note">읽는 중…</p>`;
  if (reading.rule_id === null) return `${head}<p class="pc-note">이 카테고리엔 규칙이 없어 원문 그대로 약식이 됩니다</p>`;
  return `${head}${renderSlotTable(reading, data.form.description_fills)}${readingResultOf(reading, data.form)}`;
}

/** 상자 맨 아래 한 줄 — 필수 빔 · 다시 읽는 중(막 채움) · 저장될 약식 */
function readingResultOf(reading: DescriptionReading, form: DraftForm): string {
  const missing = missingSlotRolesOf(reading, form.description_fills);
  if (missing.length > 0) return `<p class="pc-note is-missing">필수 자리가 비었습니다 — ${missing.map((role) => TOKEN_ROLE_WORD[role]).join(' · ')}. 원문에 넣거나 빈 칸을 채우세요</p>`;
  if (reading.description === null) return '<p class="pc-note">다시 읽는 중…</p>';
  return `<p class="pc-note is-found">저장될 약식 — <b>${escapeHtml(reading.description)}</b></p>`;
}

/** 번호 칸 밑 한 줄(2026-10-20 밤) — 옵션 PN 이 카탈로그에 있음(승격 대신 [요청 종료]) · 스페어 · 어셈블리만 다른 행에 있음(안내만). 새 행이면 '' */
function renderMatchLine(data: CatalogRequestDetailData, bOpen: boolean): string {
  const match = data.match;
  if (match === null || !bOpen || match.matches.length === 0) return '';
  const inCatalog = optionPartMatchOrNull(match);
  if (inCatalog !== null) {
    return `<div class="cr-match cr-match-edit">카탈로그에 이미 있음 — ${matchWordOf(inCatalog)}. 승격하지 않고 <b>[요청 종료]</b> 로 닫습니다. 행을 고칠 일은 파트넘버 옆 링크에서</div>`;
  }
  const others = match.matches.map((m) => `<li>${matchWordOf(m)}</li>`).join('');
  return `<div class="cr-match cr-match-choose">이 번호가 카탈로그의 다른 행에도 있습니다 — 같은 물건이면 승격 대신 그 행을 쓰세요<ul class="cr-match-list">${others}</ul></div>`;
}

/** 맞은 행 한 줄의 글 — 표 · 옵션 PN · 설명 · (어느 번호로) */
function matchWordOf(m: CatalogNumberMatch): string {
  const by = m.matched_column === 'option_part' ? '' : ` <span class="text-muted">(${escapeHtml(NUMBER_COLUMN_WORD[m.matched_column])} ${escapeHtml(m.matched_number)})</span>`;
  return `<span class="chip chip-muted mono">${escapeHtml(CATALOG_TABLE_WORD[m.table])}</span> <b class="mono">${escapeHtml(m.option_part)}</b>${m.description === null ? '' : ` · ${escapeHtml(m.description)}`}${by}`;
}

/** 그 카탈로그 행의 드로어 — 전체 검색이 ?q=&table= 로 그 줄을 바로 연다 */
function catalogRowHrefOf(m: CatalogNumberMatch): string {
  return catalogSearchHrefOf(m.option_part, m.table);
}

/** 루틴에 지금 맡기는 버튼 — 요청됨은 늘 [지금 조사](카테고리가 없으면 고르기에서 고른 것을 보낸다 · 안 골랐으면 alert), 확인 대기는 [다시 조사]. 끝난 요청엔 없음 */
function renderResearchButton(view: CatalogRequestView): string {
  const word = view.status === 'drafted' ? '다시 조사' : (view.status === 'requested' || view.status === 'pending') ? '지금 조사' : null;
  if (word === null) return '';
  return `<span class="pv-spacer"></span><button type="button" class="btn-ghost cr-research" data-action="research" title="LLM 이 이 건부터 바로 조사한다 — 몇 분 안에 초안">${word}</button>`;
}

/** 상태 한 마디 — 조사 중(루틴이 돌고 있다 · 칸은 열려 있고 사람이 저장한 칸은 루틴이 안 덮는다) · 지난 요청(읽기만). 열려 있고 조사 중도 아니면 '' */
function renderOpenState(view: CatalogRequestView, bOpen: boolean): string {
  if (isResearching(view)) return '<span class="chip chip-info cr-researching">LLM 조사 중 — 몇 분 뒤 확인 대기로 옵니다. 지금 적어 두는 칸은 루틴이 덮지 않습니다</span>';
  if (bOpen) return '';
  return '<span class="text-muted">지난 요청 — 읽기만</span>';
}

/** 메모 카드 — 사람의 운영 메모(notes). 어느 상태에서든 적고 [메모 저장] 으로 따로 저장한다 — 초안 칸과 섞지 않는다. 보류는 이 글을 메모로 함께 보낸다 */
function renderNotesCard(data: CatalogRequestDetailData): string {
  return `
    <div class="form-card cr-notes-card">
      <div class="form-card-head"><h2>메모</h2><small>운영 메모 — 왜 미뤘는지 · 누구에게 물었는지. 요청함 목록에 보입니다. 보류할 땐 꼭 적습니다</small></div>
      <textarea class="input cr-notes-input" data-action="notes" rows="3" placeholder="예: HPE 에 스페어 번호 확인 중 · 같은 PN 두 건 — 하나만 남기기">${escapeHtml(data.notesDraft)}</textarea>
      <div class="cr-notes-bar"><button type="button" class="btn-ghost" data-action="save-notes" ${data.saving ? 'disabled' : ''}>메모 저장</button></div>
    </div>`;
}

/** 머리 카드 — PN · 상태 칩 · 요청 글 · 출처 product · 누가 · 언제 · 왜(status_reason — 시스템 글). 오른쪽에 [지금 조사] · [다시 조사](renderResearchButton) */
function renderDetailHead(view: CatalogRequestView, bOpen: boolean): string {
  const product = view.product === null ? '' : `<span>product <a class="mono" data-link href="/products/${escapeHtml(view.product.id)}">${escapeHtml(view.product.hclt_number)}</a></span>`;
  const why = view.status_reason === null ? '' : `<div class="cr-why"><b>${view.status === 'rejected' ? '반려 이유' : '왜'}</b> ${escapeHtml(view.status_reason)}</div>`;
  return `
    <div class="form-card pd-head">
      <div class="pd-title">
        <span class="pd-pn mono">${view.option_part === null ? '(PN 없음)' : escapeHtml(view.option_part)}</span>
        ${renderPnOrigin(view)}
        <span class="pd-desc">${view.requested_text === null ? '<span class="text-muted">디스크립션 없음</span>' : escapeHtml(view.requested_text)}</span>
        ${renderStatusChip(view.status)}
        ${renderOpenState(view, bOpen)}
        ${renderResearchButton(view)}
      </div>
      <div class="pd-meta">
        <span>#${view.pending_id}</span>
        <span>카테고리 ${view.catalog_table_name === null ? '—' : escapeHtml(catalogTableWord(view.catalog_table_name))}</span>
        <span>출처 ${view.draft_source === null ? '—' : DRAFT_SOURCE_WORD[view.draft_source]}</span>
        ${product}
        <span>요청자 ${view.requested_by === null ? '—' : escapeHtml(view.requested_by)} · 요청날짜 <span class="mono">${escapeHtml(fmtDateTime(view.created_at))}</span></span>
      </div>
      ${why}
    </div>`;
}

/** 파트넘버의 출처 — 조사가 찾았으면 evidence.option_part 에 근거 url 이 있다(표 안 바꾸는 자국, 2026-10-14). 없고 PN 이 있으면 요청 때 적힌 것 */
function renderPnOrigin(view: CatalogRequestView): string {
  const found = view.spec_evidence['option_part'];
  if (found !== undefined && found !== '') return `<a class="chip chip-info cr-pn-origin" href="${escapeHtml(found)}" target="_blank" rel="noopener" title="${escapeHtml(found)}">조사가 찾은 파트넘버 ↗</a>`;
  if (view.option_part !== null) return `<span class="chip chip-muted cr-pn-origin">파트넘버 기재됨</span>`;
  return '';
}

const CELL_LABEL_MAX = 22;   // 칸 표의 열 라벨 — 주석이 문장째라 자른다(전체는 올리면 title, 현민 2026-10-20 밤)
/** 칸 표의 열 라벨 — 첫 문장을 ':' · '(' 앞까지, 길면 …. 전체 주석은 title 로 */
function cellLabelOf(column: { name: string; comment: string | null }): string {
  const full = columnLabelOf(column);
  const head = full.split(/[:(]/)[0]?.trim() ?? full;
  const label = head === '' ? full : head;
  return label.length > CELL_LABEL_MAX ? `${label.slice(0, CELL_LABEL_MAX)}…` : label;
}

/** 칸 표 — 열마다 한 줄: 이름(필수 *) · 값 · 근거 url. 표가 null 이면 안내 한 줄 */
export function renderDraftCells(form: DraftForm, columns: readonly TableColumnView[], bOpen: boolean): string {
  if (form.table === null)   return `<div class="text-muted cr-note">표를 고르면 그 표의 칸이 나옵니다.</div>`;
  if (columns.length === 0)  return `<div class="text-muted cr-note">칸을 불러오는 중…</div>`;
  const rows = columns.filter((column) => column.name !== 'option_part' && !SERVER_MADE_COLUMNS.includes(column.name)).map((column) => {
    const value = form.values[column.name] ?? '';
    const url = form.evidence[column.name] ?? '';
    const bMissing = column.required && value.trim() === '';
    const bWrongNumber = !isNumberShape(column, value.trim());
    return `
      <tr class="${bMissing || bWrongNumber ? 'cr-missing-row' : ''}">
        <td class="cr-col" title="${escapeHtml(column.comment ?? column.name)}"><span class="cr-label">${escapeHtml(cellLabelOf(column))}</span>${column.required ? ' <b class="cr-req">*</b>' : ''}<div class="cr-colname mono">${escapeHtml(column.name)}</div></td>
        <td>${renderCellInput(column, value, bOpen)}${bWrongNumber ? renderWrongNumberNote(column, value.trim()) : ''}</td>
        <td class="cr-evidence-cell">
          <input type="text" class="input cr-evidence" data-action="evidence" data-key="${escapeHtml(column.name)}" value="${escapeHtml(url)}" placeholder="근거 url" ${bOpen ? '' : 'disabled'}>
          ${url === '' ? '' : `<a class="cr-link" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="근거 열기">↗</a>`}
        </td>
      </tr>`;
  });
  return `
    <table class="pv-table pv-table-sm cr-cells">
      <thead><tr><th>열</th><th>값</th><th>근거</th></tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table>`;
}

/** 숫자 칸 꼴 틀림 한 줄 — 숫자 입력은 글('1U')을 빈칸으로 보여 줘서 초안 값을 같이 보인다 */
function renderWrongNumberNote(column: TableColumnView, text: string): string {
  return `<div class="cr-missing">${escapeHtml(numberShapeWordOf(column))} 들어갑니다 — 초안 값: <span class="mono">${escapeHtml(text)}</span></div>`;
}

/** 값 입력 하나 — 열의 꼴(cellKindOf)대로 강제한다: 정수 · 소수는 number, 예/아니오는 라디오 셋(비움 = NULL 허용 열), JSON 은 textarea, 나머지 글. 힌트(주석 뒷말)는 placeholder */
function renderCellInput(column: Pick<TableColumnView, 'name' | 'data_type' | 'comment'>, value: string, bOpen: boolean, action: string = 'cell'): string {   // 비교 표의 CompareColumn 도 쓴다(required 없음)
  const key = escapeHtml(column.name);
  const disabled = bOpen ? '' : 'disabled';
  const hint = escapeHtml(columnHintOf(column));
  switch (cellKindOf(column)) {
    case 'integer':
      return `<input type="number" step="1" class="input cr-cell" data-action="${action}" data-key="${key}" value="${escapeHtml(value)}" placeholder="${hint}" ${disabled}>`;
    case 'number':
      return `<input type="number" step="any" class="input cr-cell" data-action="${action}" data-key="${key}" value="${escapeHtml(value)}" placeholder="${hint}" ${disabled}>`;
    case 'boolean':
      return `<span class="cr-bool">${BOOL_CHOICES.map(([choice, word]) => `<label><input type="radio" name="${action}-${key}" data-action="${action}" data-key="${key}" value="${choice}" ${value === choice ? 'checked' : ''} ${disabled}> ${word}</label>`).join('')}</span>`;
    case 'json':
      return `<textarea class="input cr-cell cr-json" rows="2" data-action="${action}" data-key="${key}" placeholder="${hint}" ${disabled}>${escapeHtml(value)}</textarea>`;
    case 'text':
      return `<input type="text" class="input cr-cell" data-action="${action}" data-key="${key}" value="${escapeHtml(value)}" placeholder="${hint}" ${disabled}>`;
  }
}

/**
 * 모델 절 — 모델 FK 표(cpu · gpu · hba · network_adapter)만. 열쇠 칸(부품 칸의 model · series · chip_model) 값으로 모델 표를 찾아
 *   열쇠 비었음 → 안내 한 줄 / 모델 있음 → "있음" 칩 + 행의 값이 비어 있는 칸만 입력(나머지는 값 그대로 보임) / 없음 → "새로" 칩 + 칸 전부(필수 *).
 * 승격 하나가 모델 → 부품 순으로 쓴다 — 모델 메뉴 없음(현민 2026-10-14 밤). 근거 키는 'model.<열>'
 */
function renderModelSection(data: CatalogRequestDetailData, bOpen: boolean): string {
  const model = data.model;
  if (model === null || data.form.table === null) return '';
  const key = (data.form.values[model.key_column] ?? '').trim();
  const head = `<div class="form-card-head"><h2>모델 <span class="chip chip-muted mono">${escapeHtml(model.table)}</span></h2><small>부품이 가리키는 칩 · 프로세서 행. 같은 모델을 여러 PN 이 나눠 쓴다</small></div>`;
  if (key === '') {
    return `<div class="form-card cr-model">${head}<div class="text-muted cr-note">위 칸 <b class="mono">${escapeHtml(model.key_column)}</b> 에 모델 글을 적으면 모델 표를 찾습니다.</div></div>`;
  }
  const row = data.modelRow !== null && data.modelRow.key === key ? data.modelRow.row : undefined;   // undefined = 아직 찾는 중
  const state = row === undefined
    ? '<span class="chip chip-muted">찾는 중…</span>'
    : row === null
      ? `<span class="chip chip-warning">새로 — ${escapeHtml(key)} 가 모델 표에 없어 승격이 만든다</span>`
      : `<span class="chip chip-success">있음 — ${escapeHtml(key)}. 빈 칸만 채워진다</span>`;
  return `
    <div class="form-card cr-model">
      ${head}
      <div class="cr-model-state">${state}</div>
      ${row === undefined ? '' : renderModelCells(data.form, model, row, bOpen)}
    </div>`;
}

/** 모델 칸 표 — 열쇠 열 · vendor(부품의 oem_vendor 로 채워짐)는 뺀다. 있는 행의 찬 칸은 값 글로, 빈 칸 · 새 모델은 입력 */
function renderModelCells(form: DraftForm, model: ModelColumnsView, rowOrNull: Record<string, unknown> | null, bOpen: boolean): string {
  const rows = model.rows.filter((column) => column.name !== model.key_column && column.name !== 'vendor').map((column) => {
    const existing = rowOrNull === null ? null : rowOrNull[column.name];
    const bFilled = existing !== null && existing !== undefined && String(existing).trim() !== '';
    const value = form.model_values[column.name] ?? '';
    const evidenceKey = modelEvidenceKeyOf(column.name);
    const url = form.evidence[evidenceKey] ?? '';
    const bRequiredNew = rowOrNull === null && column.required;
    const bMissing = bRequiredNew && value.trim() === '';
    const bWrongNumber = !bFilled && !isNumberShape(column, value.trim());   // 찬 모델 칸은 값 글로만 보인다 — 입력 줄만 붉게
    const cell = bFilled
      ? `<span class="cr-model-have">${escapeHtml(String(existing))}</span>`
      : `${renderCellInput(column, value, bOpen, 'mcell')}${bWrongNumber ? renderWrongNumberNote(column, value.trim()) : ''}`;
    return `
      <tr class="${bMissing || bWrongNumber ? 'cr-missing-row' : ''}">
        <td class="cr-col" title="${escapeHtml(column.comment ?? column.name)}"><span class="cr-label">${escapeHtml(cellLabelOf(column))}</span>${bRequiredNew ? ' <b class="cr-req">*</b>' : ''}<div class="cr-colname mono">${escapeHtml(column.name)}</div></td>
        <td>${cell}</td>
        <td class="cr-evidence-cell">${bFilled ? '' : `
          <input type="text" class="input cr-evidence" data-action="mevidence" data-key="${escapeHtml(column.name)}" value="${escapeHtml(url)}" placeholder="근거 url" ${bOpen ? '' : 'disabled'}>
          ${url === '' ? '' : `<a class="cr-link" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="근거 열기">↗</a>`}`}
        </td>
      </tr>`;
  });
  return `
    <table class="pv-table pv-table-sm cr-cells">
      <thead><tr><th>열</th><th>값</th><th>근거</th></tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table>`;
}

/** 열 이름 → 한글 라벨(빠진 필수 열 글에). columns 에 없으면 이름 그대로 */
function labelOfName(columns: readonly TableColumnView[], name: string): string {
  const found = columns.find((column) => column.name === name);
  return found === undefined ? name : columnLabelOf(found);
}

/** 아래 버튼 바 — 빠진 필수 열 · 초안 저장 · 승격 · 보류(또는 보류 풀기) · 반려. 보류는 요청됨 · 확인 대기에서, 풀기는 보류에서 */
function renderDetailActions(data: CatalogRequestDetailData, bOpen: boolean, inCatalog: CatalogNumberMatch | null): string {
  if (inCatalog !== null) return renderCloseActions(data, bOpen);
  const missing = data.form.table === null ? [] : missingRequired(data.form, data.columns);
  const missingModel = missingRequiredModel(data.form, data.model, data.modelRow);
  const readingBlock = data.form.table === null ? '' : readingBlockOf(data);
  const wrongNumbers = data.form.table === null ? [] : wrongNumberColumns(data.form, data.columns);
  const wrongModelNumbers = data.form.table === null ? [] : wrongModelNumberColumns(data.form, data.model, data.modelRow);
  const bCanSave = bOpen && !data.saving && data.form.table !== null;
  const bCanPromote = bCanSave && missing.length === 0 && missingModel.length === 0 && data.form.option_part.trim() !== '' && readingBlock === '' && wrongNumbers.length === 0 && wrongModelNumbers.length === 0;
  const modelRows = data.model === null ? [] : data.model.rows;
  const missingWord = missing.length === 0 && missingModel.length === 0
    ? (data.form.option_part.trim() === '' && bOpen ? '<span class="cr-missing">승격엔 파트넘버가 필요합니다</span>' : '')
    : `<span class="cr-missing">필수 비어 있음: ${[...missing.map((name) => labelOfName(data.columns, name)), ...missingModel.map((name) => `모델 ${labelOfName(modelRows, name)}`)].map(escapeHtml).join(' · ')}</span>`;
  const orphan = data.form.table === null ? [] : orphanEvidenceLabelsOf(data.form, data.columns, data.model);
  const orphanWord = orphan.length === 0 ? '' : `<span class="cr-missing">값이 빈 칸의 근거는 저장되지 않습니다: ${orphan.map(escapeHtml).join(' · ')}</span>`;
  const readingWord = readingBlock === '' || !bOpen ? '' : `<span class="cr-missing">${escapeHtml(readingBlock)}</span>`;
  const wrongNumberTexts = [
    ...wrongNumbers.map((name) => `${labelOfName(data.columns, name)} ${data.form.values[name] ?? ''}`),
    ...wrongModelNumbers.map((name) => `모델 ${labelOfName(modelRows, name)} ${data.form.model_values[name] ?? ''}`),
  ];
  const numberWord = wrongNumberTexts.length === 0 || !bOpen ? '' : `<span class="cr-missing">숫자 칸 꼴 틀림: ${wrongNumberTexts.map(escapeHtml).join(' · ')}</span>`;
  return `
    <div class="form-card cr-actions">
      ${missingWord}${readingWord}${numberWord}${orphanWord}
      <span class="pv-spacer"></span>
      <button type="button" class="btn-ghost" data-action="save" ${bCanSave ? '' : 'disabled'}>초안 저장</button>
      <button type="button" class="btn-primary" data-action="promote" ${bCanPromote ? '' : 'disabled'}>승격</button>
      ${renderHoldButton(data)}
      <button type="button" class="btn-ghost cr-reject" data-action="reject" ${bOpen && !data.saving ? '' : 'disabled'}>반려</button>
    </div>`;
}

/** 약식 때문에 승격이 막힌 까닭 — 원문 빔 · 읽는 중 · 필수 빔. 막히지 않으면 '' */
function readingBlockOf(data: CatalogRequestDetailData): string {
  if (rawOf(data.form) === '') return '승격엔 원문이 필요합니다';
  if (data.reading === null) return '약식을 읽는 중입니다';
  const missing = missingSlotRolesOf(data.reading, data.form.description_fills);
  return missing.length === 0 ? '' : `약식 필수 자리 비어 있음: ${missing.map((role) => TOKEN_ROLE_WORD[role]).join(' · ')}`;
}

/** 옵션 PN 이 카탈로그에 있을 때의 버튼 바 — [요청 종료] 하나(중복 요청이라 반려 · 보류 · 초안 저장은 뜻이 없다, 현민 2026-10-20 밤) */
function renderCloseActions(data: CatalogRequestDetailData, bOpen: boolean): string {
  const bCan = bOpen && !data.saving;
  return `
    <div class="form-card cr-actions">
      <span class="cr-changing">카탈로그에 이미 있는 파트넘버 — 승격 대신 요청을 종료합니다</span>
      <span class="pv-spacer"></span>
      <button type="button" class="btn-primary" data-action="close-in-catalog" ${bCan ? '' : 'disabled'}>요청 종료</button>
    </div>`;
}

/** [보류] · [보류 풀기] — 보류면 풀기, 요청됨 · 확인 대기면 보류(조사 중이어도 — 보류하면 루틴 큐에서 빠진다). 끝난 요청엔 없음 */
function renderHoldButton(data: CatalogRequestDetailData): string {
  const view = data.view;
  if (view === null) return '';
  const disabled = data.saving ? 'disabled' : '';
  if (view.status === 'on_hold') return `<button type="button" class="btn-ghost" data-action="release" ${disabled}>보류 풀기</button>`;
  const bHoldable = view.status === 'requested' || view.status === 'pending' || view.status === 'drafted';
  if (!bHoldable) return '';
  return `<button type="button" class="btn-ghost" data-action="hold" ${disabled} title="메모 칸의 글과 함께 보류합니다">보류</button>`;
}
